import {useMemo} from 'react';
import {FormattedMessage, defineMessages} from 'react-intl';
import {view, Action, css} from '@sail/ui/deprecated';
import {useTheme} from '@stripe-internal/conversations-ui';
import type {RatingValue} from './types';
import {Terrible} from './Icons/Terrible';
import {Bad} from './Icons/Bad';
import {Okay} from './Icons/Okay';
import {Good} from './Icons/Good';
import {Great} from './Icons/Great';

const messages = defineMessages({
  terrible: {
    id: 'support_conversations_client.csat.rating.terrible',
    description: 'Label for CSAT rating the user can select (1/5)',
    defaultMessage: 'Terrible',
  },
  bad: {
    id: 'support_conversations_client.csat.rating.bad',
    description: 'Label for CSAT rating the user can select (2/5)',
    defaultMessage: 'Bad',
  },
  okay: {
    id: 'support_conversations_client.csat.rating.okay',
    description: 'Label for CSAT rating the user can select (3/5)',
    defaultMessage: 'Okay',
  },
  good: {
    id: 'support_conversations_client.csat.rating.good',
    description: 'Label for CSAT rating the user can select (4/5)',
    defaultMessage: 'Good',
  },
  great: {
    id: 'support_conversations_client.csat.rating.great',
    description: 'Label for CSAT rating the user can select (5/5)',
    defaultMessage: 'Great',
  },
});

type Props = {
  rating: RatingValue;
  selected: boolean;
  disabled?: boolean;
  dataTestId?: string;
  onClick: () => void;
};

export const RatingButton = ({
  rating,
  selected,
  disabled = false,
  dataTestId,
  onClick,
}: Props) => {
  const theme = useTheme();
  const contents = useMemo(() => {
    switch (rating) {
      case '1':
        return (
          <>
            <Terrible selected={selected} />
            <FormattedMessage {...messages.terrible} />
          </>
        );
      case '2':
        return (
          <>
            <Bad selected={selected} />
            <FormattedMessage {...messages.bad} />
          </>
        );
      case '3':
        return (
          <>
            <Okay selected={selected} />
            <FormattedMessage {...messages.okay} />
          </>
        );
      case '4':
        return (
          <>
            <Good selected={selected} />
            <FormattedMessage {...messages.good} />
          </>
        );
      case '5':
        return (
          <>
            <Great selected={selected} />
            <FormattedMessage {...messages.great} />
          </>
        );
      default:
        return null;
    }
  }, [rating, selected]);

  return (
    <Action
      onPress={() => onClick()}
      data-testid={dataTestId}
      disabled={disabled}
      css={{
        textAlign: css.value('center'),
        '&:focus': {
          focusRing: theme.focusVisibleOutline ? css.value('none') : undefined,
          outline: theme.focusVisibleOutline,
        },
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 150,
          minWidth: 500,
          font: 'caption',
          wordBreak: css.value('break-word'),
        }}
      >
        {contents}
      </view.div>
    </Action>
  );
};
